import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import IconSpinner from '../IconSpinner/IconSpinner';
import IconSynchronize from '../IconSynchronize/IconSynchronize';

import css from './AvailabilityToggle.module.css';

/**
 * Clickable variant of AvailabilityBadge: lets the listing owner flip the
 * "Disponibilité" status in one click, without opening the full edit listing form.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.label the text to display, e.g. "Disponible aujourd'hui"
 * @param {boolean} props.isAvailable true renders the "available" (green) style, false the "unavailable" (red) style
 * @param {boolean} props.inProgress true while the update request is in flight; disables the button and shows a spinner
 * @param {boolean} props.hasError true if the last update attempt failed; shows an inline error message
 * @param {function} props.onToggle called when the button is clicked
 * @returns {JSX.Element} clickable availability toggle
 */
const AvailabilityToggle = props => {
  const {
    className,
    rootClassName,
    label,
    isAvailable,
    inProgress,
    hasError,
    onToggle,
  } = props;
  const classes = classNames(rootClassName || css.root, className, {
    [css.available]: isAvailable,
    [css.unavailable]: !isAvailable,
  });

  return (
    <div className={css.wrapper}>
      <button
        type="button"
        className={classes}
        onClick={onToggle}
        disabled={inProgress}
        aria-busy={inProgress}
      >
        {inProgress ? (
          <IconSpinner className={css.spinner} />
        ) : (
          <>
            <span>{label}</span>
            <IconSynchronize className={css.toggleIcon} />
          </>
        )}
      </button>
      {hasError ? (
        <p className={css.error}>
          <FormattedMessage id="AvailabilityToggle.error" />
        </p>
      ) : (
        <p className={css.helperText}>
          <FormattedMessage id="AvailabilityToggle.helperText" />
        </p>
      )}
    </div>
  );
};

export default AvailabilityToggle;
